import { ApolloProvider } from "@apollo/client";
import FeatureFlagContextProvider from "@src/lib/contexts/FeatureFlagContext";
import usePollLatestVercelGitCommitSha from "@src/features/app/hooks/usePollLatestVercelGitCommitSha";
import useRefreshStaleInactiveTabs from "@src/features/app/hooks/useRefreshStaleInactiveTabs";
import useSetDashboardLoadedAtOnWindow from "@src/features/app/hooks/useSetDashboardLoadedAtOnWindow";
import { usePreserveImpersonateParam } from "@src/features/app/hooks/usePreserveImpersonateParam";
import { NextPage } from "next";
import NextAdapterPages from "next-query-params/pages";
import { QueryParamProvider } from "use-query-params";
import { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { Geist_Mono, Inter } from "next/font/google";
import { ReactElement, ReactNode, useEffect, useMemo } from "react";
import { IntercomProvider } from "react-use-intercom";
import { GoogleTagManager } from "@next/third-parties/google";

import "./global.css";
import "katex/dist/katex.min.css";
import "@src/lib/ui/pintura-overrides.css";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import smoothscroll from "smoothscroll-polyfill";
import client from "@src/lib/graphql/apollo";
import { useRouter } from "next/router";
import Head from "next/head";
import TranslationCacheContextProvider from "@src/lib/contexts/TranslationCacheContext";
import { BodyCursorTypeContextProvider } from "@src/lib/contexts/BodyCursorTypeContext";
import { DetailSidebarProvider } from "@src/lib/contexts/DetailSidebarContext";
import { FolderBreadcrumbProvider } from "@src/lib/contexts/FolderBreadcrumbContext";

const AuthedApp = dynamic(() => import("@src/features/app/AuthedApp"), {
  ssr: false,
});
const UnauthedApp = dynamic(() => import("@src/features/app/UnauthedApp"), {
  ssr: false,
});

const interVariable = Inter({
  display: "swap",
  subsets: ["latin"],
  weight: "variable",
});

const geistMono = Geist_Mono({
  display: "swap",
  subsets: ["latin"],
  weight: "variable",
});

// eslint-disable-next-line @typescript-eslint/ban-types
export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const INTERCOM_APP_ID = "nqlxuewd";
export const UNAUTHENTICATED_PATHS = ["/signup", "/ui"];

export const currentRouteIsUnauthenticated = (): boolean => {
  return (
    window &&
    !!UNAUTHENTICATED_PATHS.find((path) =>
      window.location.pathname.startsWith(path),
    )
  );
};

export const useCurrentRouteIsUnauthenticated = (): boolean => {
  const router = useRouter();
  return !!UNAUTHENTICATED_PATHS.find((path) =>
    router.pathname.startsWith(path),
  );
};

import { datadogRum } from "@datadog/browser-rum";
import type { RumEvent } from "@datadog/browser-rum";

// Noise errors drown out real ones in RUM (~145k events/14d on /library alone)
// and poison frustration signals: any click followed by one gets tagged
// error_click, making genuine UX pain invisible in the explorer.
const isNoiseRumError = (event: RumEvent): boolean => {
  if (event.type !== "error") return false;
  // Cancelled/aborted requests (status_code 0): navigation or a newer refetch
  // superseded the request, or the client went offline. Connectivity noise,
  // not app bugs — real backend failures are monitored server-side.
  if (
    event.error.source === "network" &&
    event.error.resource?.status_code === 0
  ) {
    return true;
  }
  // AbortErrors surface as uncaught JS errors (source "source", not
  // "network") when a superseded fetch is cancelled — e.g. the library-item
  // builder's 1s pollers cancelling in-flight requests (~93k events/week
  // from a handful of long-lived tabs). Expected cancellations, not bugs.
  if (
    event.error.type === "AbortError" ||
    event.error.message.includes("aborted a request") ||
    event.error.message.includes("signal is aborted")
  ) {
    return true;
  }
  // The Facebook pixel's form-action fallback is intentionally blocked by our
  // CSP (form-action 'self'); the violation report fires constantly and is
  // not a bug.
  if (
    event.error.source === "report" &&
    event.error.message.includes("facebook.com/tr")
  ) {
    return true;
  }
  return false;
};

if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production") {
  datadogRum.init({
    applicationId: "d6689222-a070-44bd-9f1c-c8a95b87fe81",
    clientToken: "pubb3142ab43d887f463629b4609eb1bec5",
    site: "datadoghq.com",
    service: "dashboard",
    env: process.env.NEXT_PUBLIC_ENV,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "allow",
    beforeSend: (event) => !isNoiseRumError(event),
  });
}

export default function _App({
  Component,
  pageProps,
}: AppPropsWithLayout): React.ReactElement {
  usePollLatestVercelGitCommitSha();
  useSetDashboardLoadedAtOnWindow();
  useRefreshStaleInactiveTabs();
  usePreserveImpersonateParam();
  useEffect(() => smoothscroll.polyfill(), []);
  const getLayout = useMemo(() => {
    return Component.getLayout ?? ((page: React.ReactNode) => page);
  }, [Component.getLayout]);
  const currentRouteIsUnauthenticated = useCurrentRouteIsUnauthenticated();
  const content = useMemo(() => {
    if (currentRouteIsUnauthenticated) {
      return (
        <UnauthedApp>
          <Component {...pageProps} />
        </UnauthedApp>
      );
    } else {
      return (
        <FeatureFlagContextProvider>
          <AuthedApp>{getLayout(<Component {...pageProps} />)}</AuthedApp>
        </FeatureFlagContextProvider>
      );
    }
  }, [Component, currentRouteIsUnauthenticated, getLayout, pageProps]);
  return (
    <>
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx global>{`
        :root {
          --font-sans-v4: ${interVariable.style.fontFamily};
          --font-mono-v4: ${geistMono.style.fontFamily};
        }
      `}</style>
      <GoogleTagManager gtmId="GTM-WCL2ZV4J" />
      <QueryParamProvider adapter={NextAdapterPages}>
        <Head>
          <meta
            name="viewport"
            content="width=device-width, height=device-height, initial-scale=1, maximum-scale=1"
          />
        </Head>
        <ApolloProvider client={client}>
          <IntercomProvider
            appId={INTERCOM_APP_ID}
            autoBoot={process.env.NODE_ENV === "production"}
          >
            <TranslationCacheContextProvider>
              <BodyCursorTypeContextProvider>
                <FolderBreadcrumbProvider>
                  <DetailSidebarProvider>{content}</DetailSidebarProvider>
                </FolderBreadcrumbProvider>
              </BodyCursorTypeContextProvider>
            </TranslationCacheContextProvider>
          </IntercomProvider>
        </ApolloProvider>
      </QueryParamProvider>
    </>
  );
}
